"use client";

import { FC } from "react";
import Section from "apps/website/components/layout/Section/Section";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import { Theme } from "apps/website/maps/Theme.map";
import highlightTextWithHTML from "apps/website/utils/website/highlightTextWithHTML";
import { useTrustpilot } from "apps/website/hooks/useTrustpilot";

import TextTitle from "../../base/Text/TextTitle/TextTitle";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";
import Spacer from "../../layout/Spacer/Spacer";
import { ILink } from "../../base/Button/Button.map";
import Container from "../../layout/Container/Container";
import Image, { IImageProps } from "../../base/Image/Image";
import TextBody from "../../base/Text/TextBody/TextBody";
import TrustPilotStars from "../../misc/TrustPilotStars";
import StickerWithText from "../../base/StickerWithText/StickerWithText";
import Button from "../../base/Button/Button";

export interface ISectionFussinessHeroProps {
  theme?: Theme;
  title: string;
  subtitle: string;
  body: string;
  image: IImageProps;
  cta: ILink;
  reviewHeader: string;
  reviewFooter: string;
}

const SectionFussinessHero: FC<ISectionFussinessHeroProps> = ({ theme = "default",
  title,
  subtitle,
  image,
  cta,
  reviewHeader,
  reviewFooter,
  body,
}) => {
  const { interpolate } = useTrustpilot();
  return (
    <Section theme={ theme } component={ SectionFussinessHero.name } size={ "none" } className="overflow-hidden h-fit relative">
      <div className="relative aspect-video xl:static w-full xl:aspect-auto h-[150px] xl:h-full xl:w-1/2 xl:absolute xl:top-0 xl:right-0 xl:bg-black overflow-hidden">
        <Image
          image={image}
          alt={title}
          cover
          className="xl:-rotate-[5.762deg] xl:scale-105"
        />
      </div>
      <Container className="mx-0 xl:pr-12 xl:pl-16 w-full xl:w-1/2">
        <Grid gap="lg">
          <Column spans={12} justify="center" align="center">
            <Spacer size="lg" />
            <TextTitle
              tag="h1"
              size={1}
              align="center"
              whitespace="balance"
              data-testid="TextImageSocialProofHeroTitle"
              className="normal-case"
            ><span dangerouslySetInnerHTML={ { __html: highlightTextWithHTML({ text: ` ${title} `, display: "inline-block", rotate: true }) } }/></TextTitle>
            <Spacer size="lg" />
            <TextSubtitle
              tag="h2"
              align="center"
              size={1}
              whitespace="balance"
              data-testid="TextImageSocialProofHeroSubtitle"
            >{ subtitle }</TextSubtitle>
            <Spacer size="lg" />
            <TextBody size={1} align="center">
              { body }
            </TextBody>
            <Spacer size="lg" />
            <Button
              data-testid="TextImageSocialProofHeroButton"
              to={ cta.to }
              size="full-width"
            >{ cta.name }</Button>
            <Spacer size="lg" />
            <Grid className={"w-full"}>
              <Column align="center">
                <div className="w-[120px]">
                  <TrustPilotStars />
                </div>
                <Spacer size="md" />
                <div className="flex items-center justify-center gap-3">
                  <TextSubtitle tag="span" size={ 4 } align="center" className="border-r border-outline-grey pr-3">{ interpolate(reviewHeader) }</TextSubtitle>
                  <TextBody tag="span" size={ 3 } align="center">{ interpolate(reviewFooter) }</TextBody>
                </div>
              </Column>
            </Grid>
          </Column>
        </Grid>
      </Container>
      <div className="relative w-full xl:w-1/2 xl:ml-0">
        <div
          className="absolute inset-0 bg-white"
          style={{
            clipPath: "polygon(0 35%, 100% 29%, 100% 100%, 0 100%)",
          }}
        />
        <div
          className="absolute inset-0 bg-light-grey-75"
          style={{
            clipPath: "polygon(0 0%, 100% 0%, 100% 29%, 0 35%)",
          }}
        />
        <Container className="relative z-10">
          <div className="flex items-start justify-center gap-8 py-8">
            <StickerWithText
              sticker={{ variant: "premiumMeatStickerSand", size: "sm" }}
              title="No grains, preservatives or fillers"
            />
            <StickerWithText
              sticker={{ variant: "vetFormulatedStickerSand", size: "sm" }}
              title="7 tasty recipes keep mealtimes interesting"
            />
            <StickerWithText
              sticker={{ variant: "noGrainsStickerSand", size: "sm" }}
              title="78% of fussy cats switch in 3 days"
            />
          </div>
        </Container>
      </div>
    </Section>
  );
};

export default SectionFussinessHero;
